import {useTranslation} from 'react-i18next';
import {useApiTranslations} from '../../../../context/ApiTranslationsContext';
import {OrganizationUserController} from '../../../../api/organization/organization-user.controller';
import {UserPermissionEnum} from '../../../../model/organization/user-permission.enum';
import {
  OrganizationUsersPageHooks,
} from './organization-users-page-hooks';

export type UserPermissionEntry = {
  permission: UserPermissionEnum
  active: boolean
}

export type OrganizationUserPermissionDrawerState = {
  permissions: UserPermissionEntry[]

  createPermission: (permission: UserPermissionEnum) => Promise<void>
  deletePermission: (permission: UserPermissionEnum) => Promise<void>

  translate: (key: string) => string
  translateLocal: (key: string) => string
}

export const useOrganizationUserPermissionDrawerState = (userId: string): OrganizationUserPermissionDrawerState => {
  const {t} = useTranslation()
  const {translate: apiTranslate} = useApiTranslations()

  const [userPermissions, setUserPermissions] = OrganizationUsersPageHooks.useOrganizationUserPermissions(userId)

  const isPermissionActive = (permission: UserPermissionEnum) =>
    userPermissions.map(it => it.permission).includes(permission)

  const refreshPermissions = async () => {
    setUserPermissions(await OrganizationUsersPageHooks.fetchOrganizationUserPermissions(userId))
  }

  const createPermission = async (permission: UserPermissionEnum) => {
    await OrganizationUserController.createOrganizationUserPermissions(userId, permission)
    await refreshPermissions()
  }

  const deletePermission = async (permission: UserPermissionEnum) => {
    const permissionId = userPermissions.find(it => it.permission === permission)?.id ?? null

    if(permissionId !== null) {
      await OrganizationUserController.deleteOrganizationUserPermissions(userId, permissionId)
      await refreshPermissions()
    }
  }

  return {
    permissions: Object.values(UserPermissionEnum).map(permission => ({permission: permission, active: isPermissionActive(permission)})),
    createPermission: createPermission,
    deletePermission: deletePermission,
    translate: (key) => apiTranslate(key) || t(key),
    translateLocal: (key) => t(`organization-users.permission-drawer.${key}`)
  }
}